/*
 * AOM LUXE — enquiry popup (sections/aom-enquiry-popup.liquid)
 * Opens a native <dialog> a few seconds after the visitor arrives, once per browser.
 * - Closing it (button, Escape, backdrop) stores "dismissed".
 * - A successful Shopify contact submission stores "submitted" and shows the thank-you.
 * - A submission with errors reopens the popup with Shopify's messages and stores nothing.
 * Storage falls back from localStorage to sessionStorage to memory, so the popup
 * still works (once per page) when storage is blocked.
 */
(() => {
  if (customElements.get('aom-enquiry-popup')) return;

  const memory = {};
  const store = {
    get(key) {
      for (const area of ['localStorage', 'sessionStorage']) {
        try {
          const value = window[area].getItem(key);
          if (value !== null) return value;
        } catch (error) {
          /* storage unavailable: try the next one */
        }
      }
      return memory[key] || null;
    },
    set(key, value) {
      memory[key] = value;
      for (const area of ['localStorage', 'sessionStorage']) {
        try {
          window[area].setItem(key, value);
          return;
        } catch (error) {
          /* storage unavailable: try the next one */
        }
      }
    },
  };

  const RETRY_MS = 3000;

  class AomEnquiryPopup extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.ready = true;

      this.key = this.dataset.storageKey || 'aom-luxe:enquiry-popup';
      this.delay = Number(this.dataset.delay) || 5000;
      this.designMode = this.hasAttribute('data-design-mode');

      this.querySelectorAll('[data-aom-popup-close]').forEach((button) =>
        button.addEventListener('click', () => this.dialog.close())
      );
      // clicks on the backdrop land on the <dialog> itself
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      // single exit path for the close button, Escape and the backdrop
      this.dialog.addEventListener('close', () => this.onClose());

      if (this.designMode) {
        this.bindEditor();
        return;
      }

      // Shopify renders the result of a popup submission into this form
      const success = this.querySelector('.aom-contact-form__status--success');
      const error = this.querySelector('.aom-contact-form__status--error');
      if (success) {
        store.set(this.key, 'submitted');
        this.cleanUrl();
        this.open();
        return;
      }
      if (error) {
        this.open();
        return;
      }

      if (store.get(this.key)) return;
      this.schedule(Math.max(0, this.delay - performance.now()));
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.unlockScroll();
      if (this.onSelect) {
        document.removeEventListener('shopify:section:select', this.onSelect);
        document.removeEventListener('shopify:section:deselect', this.onDeselect);
      }
    }

    schedule(ms) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.tryOpen(), ms);
    }

    // wait politely: never interrupt typing, an open drawer/modal or a hidden tab
    tryOpen() {
      if (store.get(this.key)) return;
      if (document.visibilityState === 'hidden') {
        document.addEventListener('visibilitychange', () => this.schedule(1000), { once: true });
        return;
      }
      const active = document.activeElement;
      const typing = active && active.matches('input, textarea, select, [contenteditable="true"]');
      const busy = document.querySelector('dialog[open]') || /overflow-hidden/.test(document.body.className);
      if (typing || busy) {
        this.schedule(RETRY_MS);
        return;
      }
      this.open();
    }

    async open() {
      if (this.dialog.open) return;
      await this.stylesReady();
      if (this.dialog.open) return;

      this.returnFocus = document.activeElement;
      this.lockScroll();
      this.dialog.showModal();
      this.dialog.classList.add('is-visible');
      // land on the dialog itself (announced by its name), not on the close button;
      // a Shopify result message keeps the focus its autofocus gave it
      if (!this.dialog.contains(document.activeElement) || document.activeElement === this.dialog.querySelector('[data-aom-popup-close]')) {
        this.dialog.focus({ preventScroll: true });
      }
    }

    onClose() {
      this.dialog.classList.remove('is-visible');
      this.unlockScroll();
      if (!this.designMode && store.get(this.key) !== 'submitted') store.set(this.key, 'dismissed');

      const target = this.returnFocus;
      this.returnFocus = null;
      if (target && target !== document.body && target.isConnected && typeof target.focus === 'function') {
        target.focus({ preventScroll: true });
      }
    }

    lockScroll() {
      const root = document.documentElement;
      const scrollbar = window.innerWidth - root.clientWidth;
      if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
      root.classList.add('aom-popup-open');
    }

    unlockScroll() {
      document.documentElement.classList.remove('aom-popup-open');
      document.body.style.paddingRight = '';
    }

    // the popup stylesheets load without blocking the page; open only once they apply
    stylesReady() {
      const pending = [...document.querySelectorAll('link[data-aom-popup-css]')].filter((link) => link.media !== 'all');
      if (pending.length === 0) return Promise.resolve();
      return Promise.race([
        Promise.all(
          pending.map(
            (link) =>
              new Promise((resolve) => {
                link.addEventListener('load', resolve, { once: true });
                link.addEventListener('error', resolve, { once: true });
              })
          )
        ),
        new Promise((resolve) => setTimeout(resolve, 4000)),
      ]);
    }

    // drop ?contact_posted=true so a reload doesn't show the thank-you again
    cleanUrl() {
      try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('contact_posted')) return;
        url.searchParams.delete('contact_posted');
        url.hash = '';
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        /* leave the URL as it is */
      }
    }

    // theme editor: open while the section is selected, never store anything
    bindEditor() {
      const id = this.dataset.sectionId;
      this.onSelect = (event) => {
        if (event.detail && event.detail.sectionId === id) this.open();
      };
      this.onDeselect = (event) => {
        if (event.detail && event.detail.sectionId === id && this.dialog.open) this.dialog.close();
      };
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);
    }
  }

  customElements.define('aom-enquiry-popup', AomEnquiryPopup);
})();
